import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FAVOURITES_PAGE_SIZE,
  getFavouritesApi,
} from '../api/favourites';
import LogoBlack from '../assets/img/MOSIDA-black.png';
import GalleryCardArt from '../components/GalleryCardArt';
import QuitLeaveModal from '../components/modals/QuitLeaveModal';
import { readAuthSession } from '../services/authSession';
import { clearKioskSession } from '../utils/kioskSession';
import { saveSelectedFavourite } from '../utils/selectedArt';
import {
  getStoredTicket,
  ticketUserId,
} from '../utils/ticket';

const SCROLL_END_PX = 160;

function resolveGalleryUserId() {
  const ticket = getStoredTicket();
  const auth = readAuthSession();
  return ticketUserId(ticket) || (auth?.user?.id ? String(auth.user.id) : '') || '';
}

function mergeUniqueFavourites(prev, next) {
  if (!next.length) return prev;
  const seen = new Set(prev.map((item) => item.id));
  const appended = next.filter((item) => item.id && !seen.has(item.id));
  return appended.length ? [...prev, ...appended] : prev;
}

/** "tracing-hand" → "Tracing Hand"; "Snap & Print" → "Print Your Own" */
function formatGalleryTag(name) {
  const spaced = String(name || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return 'Artwork';
  if (/^snap\s*&\s*print$/i.test(spaced)) return 'Print Your Own';
  return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
}

function MyGallery() {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const loadMoreLock = useRef(false);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [quitOpen, setQuitOpen] = useState(false);

  const loadPage = useCallback(async (page, { append = false } = {}) => {
    const userId = resolveGalleryUserId();
    if (!userId) {
      setItems([]);
      setHasMore(false);
      hasMoreRef.current = false;
      pageRef.current = 0;
      setError('Sign in or scan a ticket to see your gallery.');
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      return;
    }

    if (append) {
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
    }

    try {
      const result = await getFavouritesApi({
        userId,
        page,
        items: FAVOURITES_PAGE_SIZE,
      });

      pageRef.current = result.page;
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
      setItems((prev) =>
        append ? mergeUniqueFavourites(prev, result.items) : result.items
      );

      if (!append && !result.items.length) {
        setError('No favourites found yet.');
      }
    } catch (err) {
      if (!append) {
        setItems([]);
        setHasMore(false);
        hasMoreRef.current = false;
        pageRef.current = 0;
        setError(err?.message || 'Could not load your gallery.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      loadMoreLock.current = false;
    }
  }, []);

  const reload = useCallback(() => {
    pageRef.current = 0;
    hasMoreRef.current = false;
    loadMoreLock.current = false;
    setHasMore(false);
    setItems([]);
    loadPage(1, { append: false });
  }, [loadPage]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadMore = useCallback(() => {
    if (!hasMoreRef.current || loadingMoreRef.current || loading) return;
    loadPage(pageRef.current + 1, { append: true });
  }, [loadPage, loading]);

  const handleScroll = useCallback(
    (event) => {
      if (!hasMoreRef.current || loadingMoreRef.current || loading) return;
      const el = event.currentTarget;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining > SCROLL_END_PX) {
        loadMoreLock.current = false;
        return;
      }
      if (loadMoreLock.current) return;
      loadMoreLock.current = true;
      loadMore();
    },
    [loadMore, loading]
  );

  const confirmQuit = () => {
    clearKioskSession();
    setItems([]);
    setHasMore(false);
    setError('');
    setQuitOpen(false);
    navigate('/', { replace: true, state: { sessionResetAt: Date.now() } });
  };

  const handleSelect = (item) => {
    saveSelectedFavourite(item);
    navigate('/product');
  };

  return (
    <div className="galleryPage">
      <header className="galleryPage__header">
        <img className="galleryPage__logo" src={LogoBlack} alt="MOSIDA" />
        <button type="button" className="galleryPage__quit" onClick={() => setQuitOpen(true)}>
          Quit
        </button>
      </header>

      <h1 className="galleryPage__title">SELECT AN ART TO CHECKOUT</h1>

      <nav className="galleryPage__tabs" aria-label="Gallery sections">
        <button type="button" className="galleryPage__tab is-active" aria-current="page">
          MY GALLERY
        </button>
        <button
          type="button"
          className="galleryPage__tab"
          onClick={() => navigate('/my-order')}
        >
          MY ORDERS
        </button>
      </nav>

      <div
        className="galleryPage__scroll"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {loading ? (
          <div className="galleryPage__loader" role="status" aria-live="polite">
            <span className="galleryPage__spinner" aria-hidden="true" />
            <p className="galleryPage__status">Loading your gallery…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="galleryPage__empty">
            <p className="galleryPage__status">{error || 'No favourites found.'}</p>
            <button type="button" className="galleryPage__retry" onClick={reload}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="galleryGrid">
              {items.map((item, index) => {
                const label = formatGalleryTag(item.type);
                return (
                  <article key={item.id} className="galleryCard">
                    <span className="galleryCard__tag">{label}</span>
                    <GalleryCardArt
                      imageUrl={item.imageUrl}
                      alt={label}
                      priority={index < 4}
                    />
                    <button
                      type="button"
                      className="galleryCard__select"
                      onClick={() => handleSelect(item)}
                    >
                      <span>SELECT</span>
                      <span className="galleryCard__arrow" aria-hidden="true">
                        →
                      </span>
                    </button>
                  </article>
                );
              })}
            </div>
            {loadingMore ? (
              <div className="galleryPage__more" role="status" aria-live="polite">
                <span className="galleryPage__spinner galleryPage__spinner--sm" aria-hidden="true" />
                <span>Loading more…</span>
              </div>
            ) : null}
            {!hasMore && items.length > 0 ? (
              <p className="galleryPage__more galleryPage__more--end">End of gallery</p>
            ) : null}
          </>
        )}
      </div>

      <QuitLeaveModal
        show={quitOpen}
        onStay={() => setQuitOpen(false)}
        onLeave={confirmQuit}
      />
    </div>
  );
}

export default MyGallery;
